/**
 * API COVERAGE — guided setup on Home (Module 0, O11)
 *   GET  /api/setup-guide               → setupGuideApi.get
 *   POST /api/setup-guide/{key}/skip     → setupGuideApi.skip
 *   POST /api/setup-guide/{key}/resume   → setupGuideApi.resume
 *   POST /api/email-settings/test        → channelTestApi.email
 *   POST /api/whatsapp-settings/test     → channelTestApi.whatsApp
 *   POST /api/assistant/ask              → assistantApi.ask
 */
import { api } from '@/lib/apiClient';

export type SetupStepStatus = 'Done' | 'Skipped' | 'Todo';
export type SetupTrialStatus = 'Trial' | 'Ended' | 'Paid' | 'None';

export interface SetupStep {
  key: string;
  title: string;
  minutes: number;
  path: string;
  tip: string;
  status: SetupStepStatus;
  doneAt?: string | null;
  /** False while the step's module is not built or not on: listed, but cannot be started yet. */
  available: boolean;
}

export interface SetupGuide {
  companyName?: string | null;
  trial: { endsAt?: string | null; daysLeft: number; status: SetupTrialStatus; kycApproved: boolean; kycStatus?: string };
  steps: SetupStep[];
  doneCount: number;
  total: number;
  completed: boolean;
  channels: { emailTested: boolean; whatsAppTested: boolean };
}

export interface ChannelTestResult {
  channel: 'email' | 'whatsapp';
  sentTo: string;
  message: string;
}

export const setupGuideApi = {
  get: () => api.get<SetupGuide>('/setup-guide'),
  skip: (key: string) => api.post<SetupGuide>(`/setup-guide/${encodeURIComponent(key)}/skip`),
  resume: (key: string) => api.post<SetupGuide>(`/setup-guide/${encodeURIComponent(key)}/resume`),
};

export const channelTestApi = {
  email: (toEmail?: string) => api.post<ChannelTestResult>('/email-settings/test', { toEmail: toEmail || null }),
  whatsApp: (toPhone: string) => api.post<ChannelTestResult>('/whatsapp-settings/test', { toPhone }),
};

export interface AssistantAnswer {
  answer: string;
  /** A screen to open ("Take me there"), or null. */
  link?: string | null;
  linkLabel?: string | null;
  /** The clicks to do it ("Show me step by step"). */
  steps: string[];
}

export const assistantApi = {
  ask: (question: string, currentPath?: string) =>
    api.post<AssistantAnswer>('/assistant/ask', { question, currentPath: currentPath ?? null }, { timeout: 90_000 }),
};
